import * as THREE from 'three';
import { BoxCollider } from './physics/index.js';

/**
 * Procedural Stylized Fortnite-style 3D Vehicle Generator
 * Builds high-fidelity pickup trucks, sedans, and delivery vans with materials,
 * wheels, glass windows, chrome bumpers, and physics colliders.
 */

export function createVehicleMesh(type = 'pickup', options = {}) {
  const group = new THREE.Group();
  group.name = `vehicle_${type}`;

  const bodyColor = options.color || (type === 'pickup' ? 0x2b6e68 : type === 'sedan' ? 0xd4a017 : 0x3b5998);

  const paintMat = new THREE.MeshStandardMaterial({
    color: bodyColor,
    roughness: 0.35,
    metalness: 0.3
  });

  const darkMat = new THREE.MeshStandardMaterial({
    color: 0x1a1e24,
    roughness: 0.8,
    metalness: 0.1
  });

  const chromeMat = new THREE.MeshStandardMaterial({
    color: 0xd0d7de,
    roughness: 0.15,
    metalness: 0.9
  });

  const glassMat = new THREE.MeshStandardMaterial({
    color: 0x88ccff,
    roughness: 0.1,
    metalness: 0.9,
    transparent: true,
    opacity: 0.75
  });

  const lightMat = new THREE.MeshBasicMaterial({ color: 0xfffaed });
  const redLightMat = new THREE.MeshBasicMaterial({ color: 0xff2222 });

  // Helper to add wheel
  function addWheel(x, y, z) {
    const wheelGeo = new THREE.CylinderGeometry(0.38, 0.38, 0.28, 16);
    const wheelMesh = new THREE.Mesh(wheelGeo, darkMat);
    wheelMesh.rotation.z = Math.PI / 2;
    wheelMesh.position.set(x, y, z);
    wheelMesh.castShadow = true;

    // Hubcap
    const capGeo = new THREE.CylinderGeometry(0.2, 0.2, 0.29, 12);
    const capMesh = new THREE.Mesh(capGeo, chromeMat);
    capMesh.rotation.z = Math.PI / 2;
    capMesh.position.set(x, y, z);
    group.add(wheelMesh);
    group.add(capMesh);
  }

  if (type === 'pickup') {
    // 1. Classic Fortnite-style Pickup Truck (like the green truck in reference photo)
    // Lower Chassis
    const chassisGeo = new THREE.BoxGeometry(1.9, 0.35, 4.4);
    const chassis = new THREE.Mesh(chassisGeo, darkMat);
    chassis.position.set(0, 0.45, 0);
    chassis.castShadow = true;
    group.add(chassis);

    // Hood / Front Engine
    const hoodGeo = new THREE.BoxGeometry(1.85, 0.65, 1.6);
    const hood = new THREE.Mesh(hoodGeo, paintMat);
    hood.position.set(0, 0.85, 1.25);
    hood.castShadow = true;
    group.add(hood);

    // Grille & Front Bumper
    const grilleGeo = new THREE.BoxGeometry(1.7, 0.45, 0.15);
    const grille = new THREE.Mesh(grilleGeo, chromeMat);
    grille.position.set(0, 0.8, 2.1);
    group.add(grille);

    const bumperGeo = new THREE.BoxGeometry(1.95, 0.2, 0.2);
    const bumper = new THREE.Mesh(bumperGeo, chromeMat);
    bumper.position.set(0, 0.45, 2.15);
    bumper.castShadow = true;
    group.add(bumper);

    // Headlights
    const hlGeo = new THREE.SphereGeometry(0.12, 12, 12);
    const hlLeft = new THREE.Mesh(hlGeo, lightMat);
    hlLeft.position.set(-0.65, 0.85, 2.12);
    const hlRight = new THREE.Mesh(hlGeo, lightMat);
    hlRight.position.set(0.65, 0.85, 2.12);
    group.add(hlLeft);
    group.add(hlRight);

    // Cabin
    const cabGeo = new THREE.BoxGeometry(1.85, 0.85, 1.35);
    const cab = new THREE.Mesh(cabGeo, paintMat);
    cab.position.set(0, 1.4, 0.0);
    cab.castShadow = true;
    group.add(cab);

    // Windshield & Windows
    const wsGeo = new THREE.BoxGeometry(1.7, 0.55, 0.1);
    const ws = new THREE.Mesh(wsGeo, glassMat);
    ws.position.set(0, 1.45, 0.68);
    ws.rotation.x = -0.2;
    group.add(ws);

    const rearWinGeo = new THREE.BoxGeometry(1.6, 0.45, 0.1);
    const rearWin = new THREE.Mesh(rearWinGeo, glassMat);
    rearWin.position.set(0, 1.45, -0.68);
    group.add(rearWin);

    // Side windows
    const sideWinGeo = new THREE.BoxGeometry(0.1, 0.45, 0.85);
    const sideWinL = new THREE.Mesh(sideWinGeo, glassMat);
    sideWinL.position.set(-0.93, 1.45, 0);
    const sideWinR = new THREE.Mesh(sideWinGeo, glassMat);
    sideWinR.position.set(0.93, 1.45, 0);
    group.add(sideWinL);
    group.add(sideWinR);

    // Truck Bed
    const bedFloorGeo = new THREE.BoxGeometry(1.85, 0.25, 1.6);
    const bedFloor = new THREE.Mesh(bedFloorGeo, paintMat);
    bedFloor.position.set(0, 0.65, -1.35);
    bedFloor.castShadow = true;
    group.add(bedFloor);

    // Bed Side Walls
    const bedWallGeo = new THREE.BoxGeometry(0.15, 0.55, 1.6);
    const bedWallL = new THREE.Mesh(bedWallGeo, paintMat);
    bedWallL.position.set(-0.85, 0.95, -1.35);
    bedWallL.castShadow = true;
    const bedWallR = new THREE.Mesh(bedWallGeo, paintMat);
    bedWallR.position.set(0.85, 0.95, -1.35);
    bedWallR.castShadow = true;
    group.add(bedWallL);
    group.add(bedWallR);

    // Tailgate
    const tailgateGeo = new THREE.BoxGeometry(1.85, 0.55, 0.15);
    const tailgate = new THREE.Mesh(tailgateGeo, paintMat);
    tailgate.position.set(0, 0.95, -2.15);
    tailgate.castShadow = true;
    group.add(tailgate);

    // Taillights
    const tlGeo = new THREE.BoxGeometry(0.15, 0.15, 0.05);
    const tlL = new THREE.Mesh(tlGeo, redLightMat);
    tlL.position.set(-0.8, 0.95, -2.23);
    const tlR = new THREE.Mesh(tlGeo, redLightMat);
    tlR.position.set(0.8, 0.95, -2.23);
    group.add(tlL);
    group.add(tlR);

    // 4 Wheels
    addWheel(-0.95, 0.38, 1.25);
    addWheel(0.95, 0.38, 1.25);
    addWheel(-0.95, 0.38, -1.35);
    addWheel(0.95, 0.38, -1.35);

  } else if (type === 'sedan') {
    // 2. Yellow Classic Sedan (like yellow car in reference photo)
    // Lower body
    const bodyGeo = new THREE.BoxGeometry(1.8, 0.55, 4.2);
    const body = new THREE.Mesh(bodyGeo, paintMat);
    body.position.set(0, 0.65, 0);
    body.castShadow = true;
    group.add(body);

    // Cabin
    const cabinGeo = new THREE.BoxGeometry(1.65, 0.65, 2.2);
    const cabin = new THREE.Mesh(cabinGeo, paintMat);
    cabin.position.set(0, 1.2, -0.15);
    cabin.castShadow = true;
    group.add(cabin);

    // Windshields
    const wsGeo = new THREE.BoxGeometry(1.5, 0.5, 0.1);
    const wsFront = new THREE.Mesh(wsGeo, glassMat);
    wsFront.position.set(0, 1.2, 0.98);
    wsFront.rotation.x = -0.3;
    const wsBack = new THREE.Mesh(wsGeo, glassMat);
    wsBack.position.set(0, 1.2, -1.28);
    wsBack.rotation.x = 0.3;
    group.add(wsFront);
    group.add(wsBack);

    // Bumpers
    const fBumpGeo = new THREE.BoxGeometry(1.85, 0.18, 0.15);
    const fBump = new THREE.Mesh(fBumpGeo, chromeMat);
    fBump.position.set(0, 0.45, 2.12);
    const rBump = new THREE.Mesh(fBumpGeo, chromeMat);
    rBump.position.set(0, 0.45, -2.12);
    group.add(fBump);
    group.add(rBump);

    // Headlights
    const hlGeo = new THREE.SphereGeometry(0.1, 10, 10);
    const hlL = new THREE.Mesh(hlGeo, lightMat);
    hlL.position.set(-0.65, 0.7, 2.12);
    const hlR = new THREE.Mesh(hlGeo, lightMat);
    hlR.position.set(0.65, 0.7, 2.12);
    group.add(hlL);
    group.add(hlR);

    // 4 Wheels
    addWheel(-0.92, 0.35, 1.2);
    addWheel(0.92, 0.35, 1.2);
    addWheel(-0.92, 0.35, -1.2);
    addWheel(0.92, 0.35, -1.2);

  } else {
    // 3. Commercial Delivery Van / Work Truck
    const cabGeo = new THREE.BoxGeometry(2.0, 1.6, 2.0);
    const cab = new THREE.Mesh(cabGeo, paintMat);
    cab.position.set(0, 1.2, 1.3);
    cab.castShadow = true;
    group.add(cab);

    const cargoGeo = new THREE.BoxGeometry(2.1, 2.2, 3.2);
    const cargoMat = new THREE.MeshStandardMaterial({ color: 0xdfdfdf, roughness: 0.4 });
    const cargo = new THREE.Mesh(cargoGeo, cargoMat);
    cargo.position.set(0, 1.5, -1.1);
    cargo.castShadow = true;
    group.add(cargo);

    const wsGeo = new THREE.BoxGeometry(1.8, 0.65, 0.1);
    const ws = new THREE.Mesh(wsGeo, glassMat);
    ws.position.set(0, 1.45, 2.32);
    group.add(ws);

    addWheel(-1.05, 0.4, 1.4);
    addWheel(1.05, 0.4, 1.4);
    addWheel(-1.05, 0.4, -1.4);
    addWheel(1.05, 0.4, -1.4);
  }

  return group;
}

export function getVehicleCollider(type = 'pickup', position = [0, 0, 0], rotationY = 0) {
  const size = type === 'pickup' ? { x: 2.2, y: 1.8, z: 4.6 } :
               type === 'sedan' ? { x: 2.0, y: 1.5, z: 4.4 } :
               { x: 2.3, y: 2.4, z: 5.2 };

  return new BoxCollider({
    name: `vehicle_${type}_collider`,
    center: { x: position[0], y: position[1] + size.y / 2, z: position[2] },
    size,
    isWalkable: false
  });
}
